@let alloc = store.allocation();
@let showCurrent = store.hasCurrentModelSleeves();
<!-- The over/under-funded pills only mean something while the advisor is rebalancing the
     arrangement that loaded. Adding or removing a model makes them describe a set-up that is
     being left behind -- see `modelSetChanged`. -->
@let driftApplies = !store.modelSetChanged();
<div class="flex flex-col gap-y-10">
  <div class="flex flex-col gap-y-5">
    <p class="text-sm text-gray">
      Enter the total amount of the account that should be allocated to models. Then adjust the
      models, values and percentages below as needed.
    </p>
  </div>

  <!-- ⚠️ INFORMATION, not an error. Choosing not to allocate to models is an ordinary request,
       so Next is untouched and nothing is blocked.

       ⚠️ MINT, not red and not grey. Red is reserved for the three genuine blockers on steps 1,
       3 and 4; grey read as disabled furniture rather than as something addressed to the reader.
       Mint means "the request" throughout this wizard, which is exactly what this is about.

       ⚠️ It fires on the GROSS figure, never on `investable` being zero. Those are two different
       states wearing the same $0.00 -- nothing freed yet, and everything already committed -- and
       an advisor who has correctly allocated every dollar must not be told to go free up funds.

       What this replaces is a silent dead end rather than a missing sentence -- see the disabled
       input below. -->
  @if (store.hasNothingToAllocate()) {
    <p class="px-15 py-10 text-sm border rounded border-mint-light bg-mint-lightest text-mint-darkest">
      No funds are available to allocate yet. Correct the allocations from positions or set-aside cash.
    </p>
  }

  <!-- ⚠️ THE SAME BANNER STEP 1 AND THE BUY STEP CARRY, word for word and class for class
       (AndyP, 2026-09-22). This is now the step where over-commitment is most often CREATED --
       the pot's ceiling moved from what has been FREED UP to what the account is WORTH, so
       asking for more than step 1 has released is an ordinary thing to type here — and it was
       the one step of the four that never said so. The strip's red figure above is easy to miss
       while the advisor is working in the grid.

       Three red messages about one condition must not look like three kinds of problem, which is
       why this is a copy rather than a variation. The wording already names the places to fix it,
       and they are the same places from here.

       ⚠️ NOT a blocker, consistent with the other two: being over-committed mid-build is an
       ordinary state and the later steps are where it is usually resolved. `allocation` refuses
       to validate and Submit is what finally declines -- see `submitBlockers`. -->
  @if (store.isOverCommitted()) {
    <p class="px-15 py-10 text-sm border rounded border-red-light bg-red-lightest text-red-dark">
      More funds are committed than the account has available. Correct the allocations from other
      positions, models or set-aside cash.
    </p>
  }

  <!-- THE POT -- the one figure the whole grid below is a split of.

       ⚠️ Above the table and outside it, because it is not a property of any row. It used to
       have no home at all: the total was whatever the rows added up to, so the advisor could
       read it off the strip but never state it, and the two editors fought over it (see
       `setModelPot`). A control the advisor types into is the fix, and it has to sit where the
       thing it governs starts.

       ⚠️ It restates a figure the strip already shows, deliberately. The strip's ALLOCATED TO
       MODELS is Σ of the rows -- the OUTCOME -- and this is the INSTRUCTION. They agree once
       the targets total 100 and disagree while the advisor is mid-edit, which is information
       rather than a contradiction: the gap is what is left to state.

       Add Model is NOT here -- it sits under the grid, where the buy step has always put Add
       Buy. Both steps add a row to a table, and one control above with its twin below made two
       screens of the same shape read as two different screens. -->
  <!-- ⚠️ `bg-gray-lightest`, the same ground the table heads use on this step and on step 1.
       An outlined panel on white read as another row of the grid that had lost its columns; the
       tint says "this is the frame the table sits inside" using a colour the advisor is already
       reading as structure rather than as a figure. -->
  <div class="flex flex-wrap items-end px-15 py-10 border rounded gap-x-20 gap-y-10 border-gray-light bg-gray-lightest">
    <div class="flex flex-col gap-y-5">
      <!-- "TOTAL Allocated to Models". The word earns its place against the grid below, where
           every row states its own allocation: without it the field reads as one more of them
           rather than as the figure they are shares of. -->
      <label class="text-xs tracking-wide uppercase text-gray-dark" for="rav2-model-pot">
        Total Allocated to Models
      </label>
      <!-- The $ sits OUTSIDE the box, as it does on Protected Cash, the buy step and the Value
           column below: an input cannot carry a prefix of its own, and a typed one would have
           to be parsed back out of the value. -->
      <div class="flex items-center gap-x-5">
        <span class="text-gray-dark">$</span>
        <!-- ⚠️ THE CEILING IS THE ACCOUNT, not what has been freed up (AndyP, 2026-09-22, from
             advisor feedback). Asking for more than step 1 has released is accepted and shows as
             a shortfall -- that is a deficit the advisor can go and sell to cover, and stating it
             is how they work. Asking for more than the account is WORTH is refused, because no
             amount of selling could ever close it. The tooltip says both.

             `max` would do nothing here regardless: this is a text input -- it has to be, to
             accept the grouped figures `appTwoDecimals` writes -- and even on a number input
             `max` does not stop typing, it only fails validation. -->
        <!-- ⚠️ NO LONGER DISABLED WHEN NOTHING IS AVAILABLE, and the reason it was is gone with
             the clamp. It was a bug fix: `setModelPot` clamped to `investable`, so with $0
             available the box took keystrokes and stored 0 -- the advisor typed a figure and
             watched it vanish. Nothing is swallowed now, so a control that used to be able to do
             nothing can do the very thing the advisor asked for.

             🛑 And disabling here would contradict the change outright: an account with nothing
             freed up yet is EXACTLY when "state the allocation you want, then go and sell for it"
             is most useful. The mint notice above still says nothing is available, which is the
             honest half of what the disable was carrying. -->
        <input id="rav2-model-pot" type="text" inputmode="decimal"
          class="text-right w-150 input is-sm"
          appSelectOnFocus appCommitOnEnter appTwoDecimals
          [class.border-red-dark]="isOverAllocated()"
          [value]="potInput()" (change)="onPot($event)"
          [title]="store.hasNothingToAllocate()
            ? 'Nothing is available to allocate yet — free up value on Review/Sell Positions first'
            : (store.investable() | currency) + ' is funded so far. More is accepted and shows as a shortfall to cover; the ceiling is ' + (store.totalValue() | currency) + ', the whole account'"
          aria-label="Total amount allocated to models" />
      </div>
    </div>

    <!-- ⚠️ THE SAME FIGURE, TYPED THE OTHER WAY. Advisors size this in percentages of the account
         ("put a third in models") as readily as in dollars, and working that out by hand against
         a six-figure total is the kind of arithmetic the wizard should be doing.

         ⚠️ The percentage is DERIVED and never stored -- see `potPercentInput`. Two stored
         figures for one amount would disagree the moment anything else moved the pot: Allocate
         Remaining, a sale on step 1, an SMA commit.

         ⚠️ OF THE ACCOUNT, not of what is investable, so it reads against TOTAL ACCOUNT VALUE in
         the strip above -- and the whole 0-100 range is reachable, because the pot is capped at
         the account rather than at what has been freed up. Asking for more than is funded is
         accepted and shows as a shortfall, which is what sends the advisor to step 1 to sell
         (AndyP, 2026-09-22). A percentage that could only reach half its own scale would have
         been a poor control for exactly the request that prompted it.

         🟢 100% caps itself: this is the pot over the account, and the pot cannot exceed the
         account, so there is no second rule here to keep in step with the dollar box.

         Enabled in lockstep with the dollar box: they are one control in two units. -->
    <div class="flex flex-col gap-y-5">
      <label class="text-xs tracking-wide uppercase text-gray-dark" for="rav2-model-pot-pct">
        Total % Allocated
      </label>
      <!-- The % sits OUTSIDE the box, mirroring the $ on the left and the percent columns in the
           grid below, where every figure is typed bare and the unit is furniture. -->
      <div class="flex items-center gap-x-5">
        <input id="rav2-model-pot-pct" type="text" inputmode="decimal"
          class="text-right w-100 input is-sm"
          appSelectOnFocus appCommitOnEnter
          [class.border-red-dark]="isOverAllocated()"
          [value]="potPercentInput()" (change)="onPotPercent($event)"
          [title]="store.hasNothingToAllocate()
            ? 'Nothing is available to allocate yet — free up value on Review/Sell Positions first'
            : 'Share of ' + (store.totalValue() | currency) + ' — the whole account. Above what is funded shows as a shortfall to free up on Review/Sell Positions; 100% is the ceiling'"
          aria-label="Total percentage of the account allocated to models" />
        <span class="text-gray-dark">%</span>
      </div>
    </div>

    <!-- ⚠️ Only when there is something to do. It was briefly always on screen, disabled when
         it had nothing to do; a permanently visible control invites a click on a step where most
         of the time there is nothing left to apply.

         ⚠️ The AMOUNT is on the icon, not in the label -- see `remainingLabel`. A button whose
         text carries a live figure is a button that changes width on every keystroke, and this
         one sits beside the field that drives it. -->
    @if (canApplyRemaining()) {
      <div class="flex items-center gap-x-5">
        <button type="button" class="button is-sm"
          [class.is-mint]="!isOverAllocated()"
          [class.is-alert]="isOverAllocated()"
          [attr.aria-label]="remainingHint()"
          (click)="store.useAllInvestable()">
          {{ remainingLabel() }}
        </button>
        <!-- `aria-hidden`, because the button already carries this sentence as its accessible
             name. Announcing it twice would read the amount out at every focus stop. -->
        <i class="material-icons cursor-help text-gray-dark" style="font-size: 1.8rem"
          [title]="remainingHint()" aria-hidden="true">info_outline</i>
      </div>
    }

    <!-- ⚠️ Reset stays on the RIGHT, away from the pot, because it undoes the whole grid and not
         just this field. `ml-auto` rather than `justify-between` on the row: the description
         above wraps, and space-between would drag it around as it did.

         SOLID, like every other button on the step. It was outlined to mark it as secondary,
         but next to two filled buttons the outline read as the disabled state of one rather
         than as a lighter-weight control -- and it is not disabled, it is the only way back.

         Its own real disabled state carries the distinction instead: greyed and unclickable
         when there is nothing to undo. -->
    <button type="button" class="flex items-center ml-auto button is-mint is-sm gap-x-5"
      [disabled]="!store.allocationChanged()"
      title="Put the model rows and the allocated amount back as they loaded. Does not re-query Orion."
      (click)="store.resetAllocation()">
      <i class="material-icons" style="font-size: 1.6rem" aria-hidden="true">refresh</i>
      Reset
    </button>
  </div>

  <!-- ⚠️ No rows means NO TABLE, not an empty one.

       The grid used to open with a single blank row on an account holding no models, on the
       reasoning that there should be something to fill in rather than a bare table. That read
       as an instruction: a row with an empty model picker looks like a question the advisor has
       to answer, when allocating to models is optional -- the money can go to custom holdings
       or be set aside instead. Advisors filed models they did not want because the screen
       appeared to require one.

       A header with nothing under it is no better, so the whole table goes and Add Model is the
       only thing on screen. That is the way in, and it says plainly that the list starts empty.
       {@link ReallocationV2Store.allocation} has always treated an empty grid as valid.

       🟢 An account that HOLDS a model still arrives with its row -- sleeved accounts from
       Orion, and an unsleeved account carrying one at account level (ACMTECH-951). This is only
       about the case where there is genuinely nothing to show. -->
  @if (alloc.rows.length > 0) {
    <div class="w-full overflow-x-auto">
      <!-- Explicit widths, and `table-fixed` so they hold.
           
           Without them the columns size to content, and "Distribution %" / "Contribution %" are
           now long enough as labels to stretch two columns well past the 72px inputs sitting in
           them -- the short "Dist %" / "Cont %" never did. Headers wrap inside their column
           rather than forcing it wider, which is what a stacked two-line label is for. -->
      <!-- ⚠️ A min-width as well as percentages, because `table-fixed` ENFORCES the widths:
           in a container narrower than the inputs need, the percentages would squeeze the
           boxes and their $ / % adornments rather than the table overflowing. The floor is
           roughly the sum of what each column actually has to hold, and the wrapper above is
           already `overflow-x-auto`, so a narrow screen scrolls -- which is the behaviour the
           header comment describes for figures that cannot be made narrow and legible.

           ⚠️ 92rem, then 84rem: Current Value became a line under the model name, so the floor
           lost a column and the Model column took the width back. It is a floor, not a target --
           it has to come DOWN when a column goes, or the table keeps scrolling at a width it no
           longer needs. -->
      <table class="w-full text-sm border-collapse table-fixed" style="min-width: 84rem">
        <colgroup>
          <!-- 24%, up from 22%: the model name now carries a second line under it. -->
          <col style="width: 24%" />
          @if (showCurrent) {
            <col style="width: 10%" />
            <col style="width: 10%" />
          }
          <!-- Target first, then Value: 12% for the % box, 15% for the wider $ box. The
               widths travel with the columns they size. -->
          <col style="width: 12%" />
          <col style="width: 15%" />
          <col style="width: 12%" />
          <col style="width: 12%" />
          <col style="width: 5%" />
        </colgroup>
        <thead>
          <!-- One header row, and no CURRENT / REQUESTED group row above it.

               The old shape put four read-only columns under "Current" and four inputs under
               "Requested", each group on its own tinted band. It told the advisor which HALF
               they were in but not which figure paired with which: Current Target sat three
               columns away from the target it was to be compared against.

               So the pair is adjacent now -- Current Target beside New Target -- and the
               labels carry the words the group row used to, which is what makes the group row
               redundant rather than merely absent.

               ⚠️ The current VALUE is no longer a column. It is a reference line under the
               model name, because it was the one current figure with no counterpart to be
               compared against: the requested value is stated outright, not adjusted from the
               old one. So it was a column's worth of width spent on labelling the row -- which
               is what the model name does. The two percentage columns stay columns because
               their pairing is real.

               Current Distribution and Contribution are gone entirely. They were read-only
               reference nobody was comparing against: the advisor states those two outright
               rather than adjusting them from a previous figure.

               ⚠️ The GREY column bands are gone with it. Two flat greys -- the current band and
               the totals row -- plus mint made the table read as three competing zones. Current
               figures are now told apart by BEING quieter (muted, unboxed, on the page's own
               white) against inputs that are boxed and mint. Type and tint do the work that fill
               was doing badly.

               Mint runs from the header down through the totals row, so the four columns the
               advisor is being asked to fill read as one continuous zone. -->
          <tr class="text-xs tracking-wide uppercase text-gray-dark">
            <th class="p-8 text-left align-bottom border-b border-gray-lighter">Model</th>
            @if (showCurrent) {
              <!-- "Current Target" / "New Target", which is where this ended up after trying
                   it without the words.

                   The argument for dropping them: with only two read-only columns left, sitting
                   quiet and unboxed against four boxed mint ones, the current/requested split
                   reads off the styling, so the words were spending header width restating what
                   the columns already look like.

                   ⚠️ What that missed is that it left TWO columns headed "Target", separated by
                   Actual Target and told apart by nothing in the header row itself. A heading has
                   to work read on its own. "Adj. Target" fixed the collision but abbreviated the
                   one word carrying the meaning, so both halves are spelled out instead. -->
              <th class="p-8 text-center align-bottom border-b border-gray-lighter text-gray">
                Current Target
              </th>
              <!-- What the account actually holds, beside what it says it should. The pair is
                   the whole point: Current Target is Orion's stated mix, this is the mix as it
                   stands, and the figure beneath is the gap. -->
              <!-- "Actual Allocation": what the account actually holds, against the Current
                   Target beside it that is only stated.

                   ⚠️ This was "Actual Target" (AndyP, 2026-09-11 reversing an earlier call of
                   his own). The argument for "Target" was that it read as the pair's other half
                   in a table already saying "target" four times. The argument against is
                   stronger: this column is NOT a target. It is the mix as it stands, and calling
                   a measurement a target is the one thing a column of percentages beside four
                   real targets must not do. The repetition was the smaller problem. -->
              <th class="p-8 text-center align-bottom border-b border-gray-lighter text-gray">
                Actual Allocation
              </th>
            }
            <!-- A hairline where the account STOPS being described and the request starts,
                 instead of a filled band. The two current figures read together as one block,
                 and everything mint from here is what the advisor is stating.

                 ⚠️ Every column but Model is CENTRED -- header, figure and total alike.

                 Right-aligning them broke once the $ and % adornments went in: the % sits flush
                 to the cell edge, so the BOX it belongs to ends up offset left of the header
                 above it and the total below it. Three things that describe the same column
                 stopped lining up with each other.

                 Centring realigns all three on the box, and it holds because every row's
                 content is the same width -- same input, same adornment. `tabular-nums` on the
                 read-only figures keeps their digits even, which is what centring costs there:
                 a right edge would line the decimal points up down the column, and it does not.
                 Judged worth it for the column reading as one thing. -->
            <!-- "New Target" / "New Value". These four columns are the request: boxed, mint,
                 and the only things on the row the advisor can type into -- and the word is here
                 because the STYLING saying so is not enough for a heading read on its own.

                 ⚠️ Both alternatives were tried and are worse. A bare "Target" collided with
                 Current Target two columns to the left; "Adj. Target" resolved the collision but
                 abbreviated the only word carrying the distinction. "New" is short enough not to
                 wrap against the two-line headers beside it, and it pairs cleanly with the
                 Current Target it is read against. -->
            <!-- ⚠️ TARGET FIRST, and Value second. The order was the other way round, from
                 back when Value was the figure the advisor typed and the percentage was derived
                 from it. It is the reverse now: Target % is the instruction and Value is the
                 same instruction in dollars, so the request reads left to right in the order it
                 is stated -- and the requested target lands next to the stated one it is being
                 changed from.

                 ⚠️ The hairline moves with it. `border-l` marks where the account stops being
                 described and the request starts, so it belongs on whichever mint column comes
                 first; left on Value it would have drawn a line down the middle of the band. -->
            <th class="p-8 text-center align-bottom border-b border-l border-gray-lighter text-mint-darkest bg-mint-lightest">
              New Target
            </th>
            <th class="p-8 text-center align-bottom border-b border-gray-lighter text-mint-darkest bg-mint-lightest">
              New Value
            </th>
            <th class="p-8 text-center align-bottom border-b border-gray-lighter text-mint-darkest bg-mint-lightest">
              Distribution %
            </th>
            <th class="p-8 text-center align-bottom border-b border-gray-lighter text-mint-darkest bg-mint-lightest">
              Contribution %
            </th>
            <th class="p-8 border-b border-gray-lighter"></th>
          </tr>
        </thead>
        <tbody>
          @for (row of alloc.rows; track row.id) {
            <!--
              The bottom border moves to the error panel when there is one, so the panel
              reads as part of this row rather than as the top of the next one.
            -->
            <tr [class.border-b]="!row.errors.length" class="border-gray-lightest">
              <!-- A model the account ALREADY holds is not editable. Swapping one model for
                   another in place hides two instructions inside one gesture -- exit A, enter B --
                   and the request has to carry them separately for the trade team to action.
                   Changing your mind means removing the row and adding the new model, which says
                   exactly that.

                   `orionConnectAccountId` is the discriminator, not `sleeveModelName`: it is
                   null only on a row the advisor added, and it is the same field `isNew` is
                   derived from at submit, so the two cannot disagree. -->
              @if (row.orionConnectAccountId != null) {
                <td class="p-8">
                  <span class="font-semibold text-gray-darkest">{{ store.modelName(row.modelId) || row.sleeveModelName }}</span>
                  <!-- A sleeve whose model has no counterpart in the CRM list. It cannot be
                       resolved from here any more, so say so rather than showing an empty cell
                       the advisor cannot act on -- the row has to be removed. -->
                  @if (row.modelId === '') {
                    <span class="block text-xs text-red-dark">Not in your model list — remove this row</span>
                  }
                  <!-- WHAT THIS SLEEVE HOLDS TODAY, as a reference line rather than a column.

                       ⚠️ Only on a row with a sleeve behind it. The column version had to render
                       an em dash on an advisor-added row, because a table cell cannot be absent
                       and 0.00 would have read as "this model holds nothing today" instead of
                       "this model is not in the account". A line under a name simply is not
                       there, so the case disappears rather than needing a placeholder -- and the
                       `@else` branch below, which is that row, never shows one at all.

                       "Total Value:" is doing the work the column header did. Without a label
                       the figure is just a number under a name, and the advisor has no way to
                       know it is the account as it stands rather than something being proposed.

                       ⚠️ TOTAL, because there is a New Value column too. The bare word would
                       have named both the figure being stated and the figure it is stated
                       against, and this is the one the request is measured from.

                       ⚠️ The dollar over/under figure that rode beside this is GONE. It said the
                       same thing as the gap under Actual Target, in the units that matter less:
                       an advisor restating a mix is working in percentages, and the dollars are
                       what the wizard then computes for them. One statement of the drift, in the
                       column whose question it answers -- see `actualPctDeltaLabel`.

                       tabular-nums so the figures still line up down the column of names, which
                       is what let this stop being a column without becoming unreadable. -->
                  @if (showCurrent && row.sleeveCurrentValue != null) {
                    <span class="block mt-1 text-xs text-gray" style="font-variant-numeric: tabular-nums">
                      Total Value: {{ row.sleeveCurrentValue | currency }}
                    </span>
                  }
                </td>
              } @else {
                <td class="p-8">
                  <!-- 🔴 `w-full`, OR THIS SPILLS INTO THE NEXT COLUMN (AndyP, 2026-10-12). A native
                       <select> with no width sizes to its WIDEST OPTION, and the options here are
                       full model names -- "Alphastar Structured Growth SMA" and the like. The
                       shared `.select` class sets `min-w-96` and no width, which is why every
                       other select in this wizard carries an explicit one (`w-320` on the funding
                       step); this was the only one that did not.

                       ⚠️ THE TABLE IS `table-fixed`, so the column never actually widened -- the
                       control just overflowed its cell and drew on top of the neighbour. That is
                       why it looked like a layout bug rather than a sizing one, and why it showed
                       up differently in the two cases: with no current models the overflow ran
                       into the mint block, with an existing model it covered Current Target. One
                       cause, two symptoms.

                       🟢 `w-full` rather than a fixed width, so it tracks the Model column instead
                       of needing to be kept in step with it by hand. -->
                  <select class="select is-sm w-full" (change)="onModel(row.id, $event)"
                    [class.border-red-dark]="row.modelId === ''"
                    aria-label="Select model">
                    <option value="" [selected]="row.modelId === ''">— Select model —</option>
                    @for (m of store.availableModels(row.id); track m.id) {
                      <option [value]="m.id" [selected]="m.id === row.modelId">{{ m.name }}</option>
                    }
                  </select>
                </td>
              }
              <!-- Context, not inputs. The account as it stands, read as one block, with the
                   request beginning at the hairline to their right.

                   An em dash on a row the advisor ADDED to an account that does hold models:
                   there is no sleeve behind that row, and 0.00 would read as "this model holds
                   nothing today" rather than "this model is not in the account at all". Dropped
                   entirely when NO row could have a sleeve -- see the header.

                   Muted and unfilled rather than banded grey. tabular-nums so the figures line
                   up against the boxed input beside them. -->
              @if (showCurrent) {
                <td class="p-8 text-center text-gray" style="font-variant-numeric: tabular-nums">
                  {{ row.sleeveCurrentTarget != null ? fmtPct(row.sleeveCurrentTarget) + '%' : '—' }}
                </td>
                <td class="p-8 text-center text-gray" style="font-variant-numeric: tabular-nums">
                  <!-- `!== undefined`, not the `!= null` its neighbours use. The store sets this
                       to a number or to undefined and never to null, so this is the exact test --
                       and `!= null` trips the template eqeqeq rule, which the older cells predate
                       rather than endorse. -->
                  {{ row.sleeveActualPct !== undefined ? fmtPct(row.sleeveActualPct) + '%' : '—' }}
                  <!-- The gap against Current Target, styled exactly as the dollar drift on
                       the model name's reference line: understated, colour as text only,
                       parentheses for negative. See `actualPctDeltaLabel`.

                       ⚠️ Gated on `driftApplies` for the same reason that one is -- once the
                       advisor adds or removes a model they are restructuring, and the remaining
                       sleeves' gap describes an arrangement being left behind. The FIGURE above
                       stays either way: the account really does hold that share. -->
                  @if (driftApplies && actualPctDeltaLabel(row.sleeveActualPct, row.sleeveCurrentTarget); as gap) {
                    <span class="block mt-1 whitespace-nowrap"
                      style="font-size: 1.1rem; font-variant-numeric: tabular-nums"
                      [class.text-mint-darkest]="holdsMoreThanTarget(row.sleeveActualPct, row.sleeveCurrentTarget)"
                      [class.text-red-dark]="!holdsMoreThanTarget(row.sleeveActualPct, row.sleeveCurrentTarget)"
                      [title]="holdsMoreThanTarget(row.sleeveActualPct, row.sleeveCurrentTarget)
                        ? 'This sleeve holds a bigger share of the model money than its current target calls for'
                        : 'This sleeve holds a smaller share of the model money than its current target calls for'">{{ gap }}</span>
                  }
                </td>
              }
              <!-- THE INSTRUCTION, and the first thing in the mint band -- see the header for
                   why it leads and why the hairline travels with it. -->
              <td class="p-8 bg-mint-lightest border-l border-gray-lighter">
                <div class="flex items-center justify-center gap-x-5">
                  <input type="number" min="0" max="100" step="0.01" class="w-72 text-right input is-sm no-spinner"
                    appSelectOnFocus
                    [value]="pctInput(row.effectiveTarget)" (change)="onTarget(row.id, $event)"
                    aria-label="Target percent" />
                  <span class="text-gray-dark">%</span>
                </div>
              </td>
              <!-- The $ sits OUTSIDE the box, as it does on Protected Cash and the buy step: an
                   input cannot carry a prefix of its own, and a typed one would have to be
                   parsed back out of the value.

                   ⚠️ This box states the SAME target, in dollars. What it shows is
                   `pot × target%`, and typing in it sets `value ÷ pot` as this row's target --
                   so the two boxes are one instruction in two units and cannot contradict each
                   other. They used to be separate claims settled by whichever was edited last,
                   which is also why Value used to come first.

                   ⚠️ DISABLED with an empty pot, rather than accepting a figure and dividing by
                   zero. There is no share of nothing to state; the step's blocked reason names
                   the pot, and it is one field up. -->
              <td class="p-8 bg-mint-lightest">
                <div class="flex items-center justify-center gap-x-5">
                  <span class="text-gray-dark">$</span>
                  <input type="text" inputmode="decimal" class="w-120 text-right input is-sm"
                    appSelectOnFocus
                    [disabled]="store.modelPot() <= 0"
                    [class.border-red-dark]="row.belowMinimum"
                    [value]="valueInput(row.allocatedValue)" (change)="onValue(row.id, $event)"
                    aria-label="Allocated value" />
                </div>
              </td>
              <td class="p-8 bg-mint-lightest">
                <div class="flex items-center justify-center gap-x-5">
                  <input type="number" min="0" max="100" step="0.01" class="w-72 text-right input is-sm no-spinner"
                    appSelectOnFocus
                    [value]="pctInput(row.effectiveDistribution)" (change)="onDistribution(row.id, $event)"
                    aria-label="Distribution percent" />
                  <span class="text-gray-dark">%</span>
                </div>
              </td>
              <td class="p-8 bg-mint-lightest">
                <div class="flex items-center justify-center gap-x-5">
                  <input type="number" min="0" max="100" step="0.01" class="w-72 text-right input is-sm no-spinner"
                    appSelectOnFocus
                    [value]="pctInput(row.effectiveContribution)" (change)="onContribution(row.id, $event)"
                    aria-label="Contribution percent" />
                  <span class="text-gray-dark">%</span>
                </div>
              </td>
              <!-- No rows.length > 1 guard since ACMTECH-945: the LAST row can be removed too.
                   An account moving entirely out of models -- into custom holdings or protected
                   cash -- is a legitimate request, and with the grid opening blank the advisor
                   would otherwise be stuck on a row they cannot fund and cannot delete. An empty
                   grid validates; Submit still refuses to let the money go nowhere. -->
              <td class="p-8 text-center">
                <!-- Mint, not alert-red. Removing a model row is an ordinary edit on this step -- the
                     grid is built to be added to and taken from, and the last row can go too -- so a
                     red control overstated it. Red is reserved here for the two things that
                     genuinely block: an over-allocation, and a column off 100. -->
                <button type="button" class="icon-button is-mint is-inverted" title="Remove model"
                  (click)="onRemove(row.id)">
                  <i class="material-icons" style="font-size: 1.6rem">delete</i>
                </button>
              </td>
            </tr>

            <!-- A row's errors, on their own row beneath it, in the model column. See the
                 cell below for why it is a row of its own and why the width comes from a
                 cell rather than a percentage. -->
            @if (row.errors.length) {
              <tr class="border-b border-gray-lightest">
                <!-- ⚠️ The banner is confined to the MODEL column, in its own cell, with the rest
                     of the row spanned by an empty one beside it. It used to span the whole table
                     and ran the full width of the screen for a five-word message -- pointing at
                     nothing in particular, when every error here is about the model.

                     ⚠️ A CELL rather than a width on the div, because `table-fixed` makes the
                     first cell exactly as wide as the model column is, whatever that turns out
                     to be. A hard-coded 24% to match the `<col>` would be wrong twice: the td's
                     own horizontal padding shifts it, and the `<col>` widths only sum to 100 when
                     `showCurrent` is on -- with it off they total 80 and the browser redistributes
                     the remainder, so the model column renders wider than its declared 24%.

                     Still a separate ROW, which is what the previous note here was defending and
                     is unchanged: putting the message inside the model cell of the row ITSELF
                     widened the column and shunted every input left, moving the field being typed
                     in; absolute positioning fixed the width but overlapped the row below. A
                     spanning row costs nothing horizontally and takes its own vertical space.

                     It also scales: a row can be under its minimum AND missing a model, and both
                     are shown rather than the advisor fixing one and meeting the next. -->
                <td class="px-8 pb-8 align-top">
                  <!-- px-10, not pl-16/pr-12: 16 and 12 are not on the FIG spacing scale, so
                       those classes generated no CSS and the icon sat flush against the
                       border. Same trap the tailwind config records for w-250. -->
                  <div class="flex items-start px-10 py-8 text-xs border rounded gap-x-8 border-red-lighter bg-red-lightest text-red">
                    <i class="material-icons" style="font-size: 1.4rem" aria-hidden="true">error_outline</i>
                    @if (row.errors.length === 1) {
                      <span>{{ row.errors[0] }}</span>
                    } @else {
                      <ul class="list-disc pl-15">
                        @for (error of row.errors; track error) {
                          <li>{{ error }}</li>
                        }
                      </ul>
                    }
                  </div>
                </td>
                <!-- ⚠️ The rest of the row, CELL BY CELL rather than one spanning cell, so the
                     mint band carries straight through the error row. A single `colspan` left a
                     white gap across all four mint columns wherever a row was in error -- the
                     band appeared to stop above the TOTALS line and start again below it, which
                     read as though the error row had ended the table.

                     One cell cannot be half-tinted, so each column gets its own. Empty on
                     purpose: the error is about the model, and repeating anything here would
                     invite reading it as a second row of inputs.

                     ⚠️ These must stay in step with the `<col>` list and with the cells of the
                     row above -- including `border-l` on NEW TARGET, which is what draws the
                     left edge of the mint band. -->
                @if (showCurrent) {
                  <td colspan="2"></td>
                }
                <td class="bg-mint-lightest border-l border-gray-lighter"></td>
                <td class="bg-mint-lightest"></td>
                <td class="bg-mint-lightest"></td>
                <td class="bg-mint-lightest"></td>
                <!-- The delete column is outside the band, as it is on every other row.

                     ⚠️ It also had no cell at all until now: the old spanning cell was
                     `colspan="showCurrent ? 7 : 6"` against a table of 8 columns and 6, so the
                     error row was one short whenever Current Value was showing. Invisible while
                     nothing was tinted, and exactly the kind of thing that would have shifted
                     the band by a column the moment one was. -->
                <td></td>
              </tr>
            }
          }
        </tbody>
        <tfoot>
          <!-- ⚠️ No fill. This row was `bg-gray-lightest`, which put a second flat grey under a
               table that already had one -- and it tinted the four mint columns grey exactly
               where the advisor is reading whether they total 100.

               A rule and weight say "sum" without a fill, and the mint band carries straight
               through, so each total sits in the same column colour as the boxes above it.

               Each total carries how far it is from where it has to land, so the advisor is
               told the number to apply elsewhere rather than subtracting from 100 themselves.
               Most useful when they are typing percentages by hand -- precisely when the
               auto-balancing is not doing it for them.

               Stacked under the total rather than beside it: this row shares its columns with
               the inputs above, and a wider cell here would widen the column and shunt every
               input sideways -- the same problem the error panel had. A short line underneath
               costs height, which nothing else is competing for. -->
          <tr class="font-semibold border-t border-gray-light text-gray-darkest">
            <!-- Spans Model and BOTH current columns: the account as it stands is not part of
                 what has to total 100%, and there is no current total to show. Grouping the
                 current columns together is also what let the empty cell that used to sit
                 between the new figures go.
                 ⚠️ Actual Target does total 100 by construction, and is deliberately NOT
                 summed here. The mint totals exist because those columns MUST reach 100 and the
                 advisor is being asked to get them there; a total that cannot be anything else
                 tells them nothing and would read as a fourth thing to check. -->
            <td [attr.colspan]="showCurrent ? 3 : 1" class="p-8 text-xs tracking-wide uppercase font-normal text-gray">
              Totals
            </td>
            <td class="p-8 text-center bg-mint-lightest border-l border-gray-lighter" style="font-variant-numeric: tabular-nums"
              [class.text-red-dark]="isOffTarget(alloc.totals.target)">
              {{ alloc.totals.target | number: '1.2-2' }}%
              @if (isOffTarget(alloc.totals.target)) {
                <span class="block text-xs font-normal whitespace-nowrap" [class]="deltaClass(alloc.totals.targetDelta)">
                  {{ deltaLabel(alloc.totals.targetDelta) }}
                </span>
              }
            </td>
            <!-- Plain and unannotated, unlike the three percentage totals around it. It answers
                 to the pot rather than to 100, and once the targets land on 100 it IS the pot --
                 so a delta beneath it would restate the field above the table. -->
            <td class="p-8 text-center bg-mint-lightest" style="font-variant-numeric: tabular-nums">
              {{ alloc.totals.value | currency }}
            </td>
            <td class="p-8 text-center bg-mint-lightest" style="font-variant-numeric: tabular-nums"
              [class.text-red-dark]="isOffTarget(alloc.totals.distribution)">
              {{ alloc.totals.distribution | number: '1.2-2' }}%
              @if (isOffTarget(alloc.totals.distribution)) {
                <span class="block text-xs font-normal whitespace-nowrap" [class]="deltaClass(alloc.totals.distributionDelta)">
                  {{ deltaLabel(alloc.totals.distributionDelta) }}
                </span>
              }
            </td>
            <td class="p-8 text-center bg-mint-lightest" style="font-variant-numeric: tabular-nums"
              [class.text-red-dark]="isOffTarget(alloc.totals.contribution)">
              {{ alloc.totals.contribution | number: '1.2-2' }}%
              @if (isOffTarget(alloc.totals.contribution)) {
                <span class="block text-xs font-normal whitespace-nowrap" [class]="deltaClass(alloc.totals.contributionDelta)">
                  {{ deltaLabel(alloc.totals.contributionDelta) }}
                </span>
              }
            </td>
            <!-- Plain, unannotated, and never coloured.

                 This cell knows only what the models hold, and the models are one part of the
                 account. When the figure disagrees with what is available it cannot tell whether
                 the allocation is too high or the rest of the request freed up too little -- so a
                 red total, or a signed delta beneath it, asserted a fault it was in no position
                 to attribute.

                 The imbalance is stated where it CAN be attributed: the strip's Investable term
                 carries the signed figure and its colour, and the message beside Next names every
                 way out of it. One place, not three. -->
            <td class="p-8"></td>
          </tr>
        </tfoot>
      </table>
    </div>
  }

  <!-- Under the grid, matching Add Buy on the next step. On an empty grid it is the only
       control here, which is the point: adding a model is a choice, not a form to complete.

       SOLID mint, because it is the primary action on this step -- and on an account with no
       models it is the ONLY way in, so an outlined button that read as a link was the weakest
       possible affordance for the one thing the advisor has to find. -->
  <!-- ⚠️ HIDDEN until a total has been stated (AndyP, 2026-09-15). Every row's dollars are
       DERIVED as `pot x target%`, so adding a model against a pot of $0 produces a row that can
       only ever read $0.00 -- a control that appears to work and cannot. The step's own intro
       gives the order: enter the total, then adjust the models below.

       🟢 This subsumes the empty-pool case rather than needing its own test: `setModelPot` clamps
       to `investable`, so with nothing freed the pot cannot leave 0 and the button cannot appear.

       ⚠️ HIDDEN, not dimmed, unlike the SMA case below -- and the difference is whether there is
       anything to say. An SMA lock is a rule the advisor will want explained, so that button stays
       clickable to explain itself. This is just "not yet", already stated by the notice above and
       by the total box sitting empty right there. -->
  @if (store.modelPot() > 0) {
  <div>
    <!-- ⚠️ Dimmed by CLASS while an SMA holds the account, never by the `disabled` attribute.
         `.disabled` (styles.input.scss) paints the grey and the not-allowed cursor and suppresses
         is-mint's fill, but sets no `pointer-events`, so the click still fires and `onAddModel`
         can say why. The attribute would take the button out of the tab order and swallow the
         click, leaving a grey control that explains nothing -- which is the failure mode the
         locked sell checkbox had.

         aria-disabled carries the state to assistive tech while the element stays focusable. -->
    <button type="button" class="button is-mint is-sm"
      [class.disabled]="store.smaLockActive()"
      [attr.aria-disabled]="store.smaLockActive() ? 'true' : null"
      (click)="onAddModel()">
      + Add Model
    </button>
  </div>
  }
</div>
